/**
 * /market/:slug/p/:productSlug — Página de detalle de producto (PDP).
 *
 * Storefront UX V1 · Fase 4: la UI vive en MarketProductDetail. Esta ruta solo
 * resuelve catálogo + producto y aplica el theme del tenant.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useMemo } from "react";
import { Package } from "lucide-react";
import {
  loadMarketCatalog,
  type MarketCatalogResult,
  type MarketProduct,
} from "@/lib/market-catalog";
import { projectExperience } from "@/lib/experience-definition/project";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";
import { MarketProductDetail } from "@/components/market/MarketProductDetail";
import { MarketPdpSkeleton } from "@/components/market/MarketSkeletons";
import { resolveTenantShareImage, tenantBrandIconUrl } from "@/lib/tenant-brand-image";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string; productSlug: string }) => input)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(data.slug, {
      request: getRequest(),
      productSlug: data.productSlug,
    });
  });

export const Route = createFileRoute("/market_/$slug/p/$productSlug")({
  pendingComponent: MarketPdpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) =>
    fetchCatalog({ data: { slug: params.slug, productSlug: params.productSlug } }),
  component: ProductDetailPage,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const data = result?.ok ? result.data : null;
    const product = data?.products.find((p) => p.slug === params.productSlug);
    const tenantName = data?.tenant.name ?? params.slug;
    const title = product ? `${product.name} — ${tenantName}` : `${params.productSlug} — ${params.slug}`;
    const description =
      product?.description?.trim() ||
      data?.config?.seo?.description?.trim() ||
      `Comprá ${product?.name ?? params.productSlug} en ${tenantName}`;
    const canonicalUrl = data?.tenant?.customDomain
      ? `https://${data.tenant.customDomain}/p/${params.productSlug}`
      : `https://${data?.tenant?.subdomain || data?.tenant?.slug || params.slug}.structura.com.ar/p/${params.productSlug}`;
    const origin = canonicalUrl.replace(/\/p\/.*$/, "");
    const image = resolveTenantShareImage({
      seoOgImageUrl: product?.imagenUrl,
      logoUrl: data?.tenant?.logoUrl,
      heroImageUrl: data?.config?.heroImageUrl,
      productImageUrls: (data?.products ?? []).map((p) => p.imagenUrl),
      slug: params.slug,
      origin,
    });
    const iconHref = data?.tenant?.logoUrl?.trim() || tenantBrandIconUrl(params.slug, origin);
    const exp = data?.experiencePublished;
    const heading = exp?.brand.typography.heading.family?.replace(/ /g, "+");
    const body = exp?.brand.typography.body.family?.replace(/ /g, "+");
    const families = [heading, body].filter(Boolean);
    const fontHref =
      families.length > 0
        ? `https://fonts.googleapis.com/css2?${families
            .map((f) => `family=${f}:wght@400;500;600;700;800`)
            .join("&")}&display=swap`
        : "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap";
    const lcp = (product?.imagenUrl ?? "").trim();
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:site_name", content: tenantName },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "apple-mobile-web-app-title", content: tenantName },
      ],
      links: [
        { rel: "manifest", href: `/api/manifest/${params.slug}` },
        { rel: "icon", href: iconHref },
        { rel: "apple-touch-icon", href: iconHref },
        { rel: "stylesheet", href: fontHref },
        ...(lcp
          ? [{ rel: "preload" as const, as: "image" as const, href: lcp }]
          : []),
      ],
    };
  },
});

function ProductDetailPage() {
  const { slug, productSlug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const data = result.ok ? result.data : null;
  const product = useMemo<MarketProduct | null>(() => {
    if (!data) return null;
    return data.products.find((p) => p.slug === productSlug) ?? null;
  }, [data, productSlug]);

  const brandStyle = useMemo(() => {
    if (!data) return undefined;
    const projection = (() => {
    try {
      return data.experiencePublished ? projectExperience(data.experiencePublished as never) : null;
    } catch {
      return null;
    }
  })();
    return projection?.brandStyle ?? data.brand.surfaceStyle;
  }, [data]);

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
        <p className="text-muted-foreground">{!result.ok ? result.error : ""}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="market-surface min-h-screen" style={brandStyle}>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
          <Package className="h-12 w-12 text-[var(--market-on-surface-variant)]" />
          <h1 className="market-font-display text-xl font-bold text-[var(--market-on-surface)]">
            Producto no encontrado
          </h1>
          <Link
            to="/market/$slug"
            params={{ slug }}
            className="rounded-full bg-[var(--market-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--market-on-primary,var(--primary-foreground))]"
          >
            Volver a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="market-surface min-h-screen" style={brandStyle}>
      <MarketProductDetail slug={slug} product={product} data={data} themeStyle={brandStyle} />
    </div>
  );
}
